<template>
    <div class="mt-6 bg-white rounded-md shadow-sm border border-gray-200">
        <div class="sm:col-span-6 sm:flex justify-between items-center border-b border-gray-200 py-4 px-4">
            <div>
                <h4 class="text-xl font-bold text-gray-700 sm:text-2xl sm:truncate">Partner Report</h4>
                <p class="text-sm text-gray-500">This report shows the number of properties and applications for agents/landlords in each month.</p>
            </div>
        </div>
        <div class="p-6 bg-gray-50">
            <div class="relative">
                <div v-show="showAffiliatesDropdown" class="flex mb-3">
                    <div>
                        <label class="block text-sm font-medium text-gray-700">Affiliates</label>
                        <select v-model="form.user_id" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md" aria-label="Affiliate">
                            <option value="Year" disabled>Affiliates</option>
                            <option value="">Select an Affiliate</option>
                            <option v-for="affiliate in affiliates" :value="affiliate.id" :key="affiliate.id">{{ affiliate.name }}</option>
                        </select>
                    </div>
                </div>
                <div class="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-3 sm:justify-items-stretch">
                    <div class="order-2 sm:order-1">
                        <select v-model="form.paginate" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md" aria-label="Number of records per page">
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div class="order-2 sm:order-2">
                        <select v-model="form.month" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md" aria-label="Month">
                            <option value="Month" disabled>Select Month</option>
                            <option v-for="(month, index) in months" :value="index" :key="index">{{ month }}</option>
                        </select>
                    </div>
                    <div class="order-2 sm:order-3">
                        <select v-model="form.year" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md" aria-label="Year">
                            <option value="Year" disabled>Select Year</option>
                            <option value="">All Years</option>
                            <option v-for="year in years" :value="year" :key="year">{{ year }}</option>
                        </select>
                    </div>
                    <div class="sm:col-span-4 order-4 hidden xl:flex">
                        <input id="partner-report-search" v-model="form.search" type="text" placeholder="Search by agent/landlord name or email..." class="mt-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md">
                    </div>
                </div>
                <div class="grid grid-cols-6 gap-4 mb-6 xl:hidden sm:justify-items-stretch">
                    <div class="col-span-4 md:col-span-3">
                        <input id="partner-report-search-mobile" v-model="form.search" type="text" placeholder="Search by agent name or email..." class="mt-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md">
                    </div>
                </div>
            </div>
            <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-auto max-h-900 lg:border">
                                <table class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                    <thead class="bg-blue-50 sticky top-0 z-10">
                                        <tr>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">Agent Name</th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">Agent Email</th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">Registered Date</th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">Report Date</th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">Total Properties</th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">Total Applications</th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">Paid Applications</th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">Affiliate Revenue</th>
                                        </tr>
                                    </thead>
                                    <tbody class="bg-white">
                                        <template v-for="agent in agents.data" :key="agent.name">
                                            <tr v-if="agent.monthly_data.length === 0" class="border-gray-200">
                                                <td class="whitespace-nowrap py-2 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                    {{ agent.name }}
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-center">
                                                    {{ agent.email }}
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-center">
                                                    {{ agent.created_at }}
                                                </td>
                                                <td colspan="5" class="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-center">
                                                    No applications have been processed by this agent/landlord.
                                                </td>
                                            </tr>
                                            <tr v-else v-for="(monthlyData, monthId) in agent.monthly_data" :key="monthId" :class="[monthId === 0 ? 'border-gray-300' : 'border-gray-200', 'border-t']">
                                                <td class="whitespace-nowrap py-2 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                    <span v-if="monthlyData.index === 0">{{ agent.name }}</span>
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-center">
                                                    <span v-if="monthlyData.index === 0">{{ agent.email }}</span>
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-center">
                                                    <span v-if="monthlyData.index === 0">{{ agent.created_at }}</span>
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-2 text-sm text-gray-900 font-bold text-right">
                                                    {{ monthlyData.month_formatted }}
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-center">
                                                    --
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-center">
                                                    {{ monthlyData.total_applications }}
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-center">
                                                    {{ monthlyData.paid_applications }}
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-center">
                                                    ${{ monthlyData.paid_applications * 10 }}
                                                </td>
                                            </tr>
                                            <tr class="border border-green-200 bg-green-50 font-bold">
                                                <td colspan="4"></td>
                                                <td class="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-center">
                                                    {{ agent.total_properties }}
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-center">
                                                    {{ agent.total_applications }}
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-center">
                                                    {{ agent.paid_applications }}
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-center">
                                                    ${{ agent.paid_applications * 10 }}
                                                </td>
                                            </tr>
                                            <tr class="bg-gray-50">
                                                <td colspan="8" class="whitespace-nowrap px-3 py-2 text-sm text-gray-500 text-center"></td>
                                            </tr>
                                        </template>
                                        <tr v-show="agents.data.length === 0" class="bg-white">
                                            <td colspan="8" class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                                There is no data for this search.
                                            </td>
                                        </tr>
                                    </tbody>
                                    <tfoot class="bg-blue-50 sticky bottom-0 z-10">
                                        <tr>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">Agent Name</th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">Agent Email</th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">Registered Date</th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">Report Date</th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">Total Properties</th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">Total Applications</th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">Paid Applications</th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">Affiliate Revenue</th>
                                        </tr>
                                    </tfoot>
                                </table>

                                <!-- Stacked layout for sm, md, and lg (table becomes vertical) -->
                                <ul role="list" class="lg:hidden grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 my-5 px-2">
                                    <li v-for="agent in agents.data" :key="agent.name" class="bg-white hover:outline-blue-500 hover:bg-blue-50 col-span-1 rounded-lg shadow-lg hover:outline hover:outline-offset-2 border p-6">
                                        <div v-if="agent.monthly_data.length === 0" class="flex flex-col">
                                            <h3 class="truncate text-sm font-medium text-gray-500"><span class="text-gray-900">Agent Name:</span>{{ agent.name }}</h3>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Agent Email:</span> {{ agent.email }}</h4>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Agent Register Date:</span> {{ agent.created_at }}</h4>
                                            <h4 class="mt-1 truncate text-sm text-gray-500">No applications have been processed by this agent/landlord.</h4>
                                        </div>
                                        <div v-else v-for="(monthlyData, monthId) in agent.monthly_data" :key="monthId" class="flex flex-col">
                                            <h3 v-if="monthlyData.index === 0" class="truncate text-sm font-medium text-gray-500"><span class="text-gray-900">Agent Name:</span> {{ agent.name }}</h3>
                                            <h4 v-if="monthlyData.index === 0" class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Agent Email:</span> {{ agent.email }}</h4>
                                            <h4 v-if="monthlyData.index === 0" class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Agent Register Date:</span> {{ agent.created_at }}</h4>
                                            <h4 v-if="monthlyData.index === 0" class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Total Properties:</span> {{ agent.total_properties }}</h4>

                                            <div v-if="monthlyData.index === 0" class="mt-3 flex text-center">
                                                <div class="flex w-0 flex-1">
                                                    <div class="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 py-2 text-sm font-semibold text-gray-900">
                                                        Date
                                                    </div>
                                                </div>
                                                <div class="flex w-0 flex-1">
                                                    <div class="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 py-2 text-sm font-semibold text-gray-900">
                                                        Total Apps
                                                    </div>
                                                </div>
                                                <div class="flex w-0 flex-1">
                                                    <div class="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 py-2 text-sm font-semibold text-gray-900">
                                                        Paid Apps
                                                    </div>
                                                </div>
                                                <div class="flex w-0 flex-1">
                                                    <div class="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 py-2 text-sm font-semibold text-gray-900">
                                                        Revenue
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="flex divide-x divide-gray-200">
                                                <div class="-ml-px flex w-0 flex-1">
                                                    <div class="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-2 text-sm font-semibold text-gray-900">
                                                        {{ monthlyData.month_formatted }}
                                                    </div>
                                                </div>
                                                <div class="-ml-px flex w-0 flex-1">
                                                    <div class="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-2 text-sm font-semibold text-gray-900">
                                                        {{ monthlyData.total_applications }}
                                                    </div>
                                                </div>
                                                <div class="-ml-px flex w-0 flex-1">
                                                    <div class="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-2 text-sm font-semibold text-gray-900">
                                                        {{ monthlyData.paid_applications }}
                                                    </div>
                                                </div>
                                                <div class="-ml-px flex w-0 flex-1">
                                                    <div class="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-2 text-sm font-semibold text-gray-900">
                                                        ${{ monthlyData.paid_applications * 10 }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-if="agent.monthly_data.length > 0" class="flex divide-x divide-gray-200 bg-green-50 font-bold">
                                            <div class="-ml-px flex w-0 flex-1">
                                                <div class="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-2 text-sm font-bold text-gray-900">
                                                    TOTALS
                                                </div>
                                            </div>
                                            <div class="-ml-px flex w-0 flex-1">
                                                <div class="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-2 text-sm font-bold text-gray-900">
                                                    {{ agent.total_applications }}
                                                </div>
                                            </div>
                                            <div class="-ml-px flex w-0 flex-1">
                                                <div class="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-2 text-sm font-bold text-gray-900">
                                                    {{ agent.paid_applications }}
                                                </div>
                                            </div>
                                            <div class="-ml-px flex w-0 flex-1">
                                                <div class="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-2 text-sm font-bold text-gray-900">
                                                    ${{ agent.paid_applications * 10 }}
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                    <li v-show="agents.data.length === 0" class="col-span-1 rounded-lg border bg-white p-6 text-center text-sm font-medium text-gray-500">
                                        There is no data for this search.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <pagination class="mt-6" :links="agents.links" :results="agents" :type="'agents'" />
        </div>
    </div>
</template>

<script setup>
import { reactive, watch } from 'vue'
import { router } from '@inertiajs/vue3'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'
import Pagination from '@/Shared/Pagination.vue'

const props = defineProps({
    filters: Object,
    agents: Object,
    affiliates: [Object, Array],
    showAffiliatesDropdown: Boolean,
})

const months = [
    'All Months',
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
]

function generateYears() {
    const currentYear = new Date().getFullYear()
    const startYear = 2023
    const years = []

    for (let year = currentYear; year >= startYear; year--) {
        years.push(year)
    }

    return years
}

const years = generateYears()

const form = reactive({
    user_id: props.filters.user_id ? props.filters.user_id : '',
    month: props.filters.month,
    year: props.filters.year,
    search: props.filters.search,
    paginate: props.filters.paginate ? props.filters.paginate : 25,
})

// Filtering reloads only the report props so the rest of the page (payouts,
// share links) is left untouched.
watch(
    form,
    throttle(() => {
        router.get(route('partners.index'), pickBy(form), {
            only: ['agents', 'filters'],
            preserveState: true,
            preserveScroll: true,
        })
    }, 150),
    { deep: true }
)
</script>
